'use client';
import { Bar, BarChart, CartesianGrid, XAxis } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'app-template-lib/components/shadcn';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from 'app-template-lib/components/shadcn';
// import { ChartConfig } from 'app-template-lib/components/shadcn';
import { IContentsContentElem } from 'app-template-lib/utils';
import { TContentsContentParams } from '@/shared/t-contents-params';
import { FETCH_T_PDCA_ID } from '@/features/openProjects/queries/pdca_t_pdca.queries.type';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ToWarekiFormatter } from '@/features/openProjects/utils/toWarekiFormatter';
import { FETCH_M_LG_CONFIG } from '@/main/queries/setting_m_lg_config.queries.type';

// satisfies ChartConfig;

interface IYosanChartPresenterProps<U> extends IContentsContentElem<U> {
    data: FETCH_T_PDCA_ID | undefined;
    lgconfig: FETCH_M_LG_CONFIG | undefined;
}

type JIGYOHIS = {
    jigyoyear: number;
    jigyohi: number;
    yosan_gengaku?: number;
}[];

export function YosanChartWidget({ data, lgconfig }: IYosanChartPresenterProps<TContentsContentParams>) {
    const [jigyohis, setJigyohis] = useState<JIGYOHIS>([]);
    const { lgcode, jigyoYear } = useParams<{ lgcode: string; jigyoYear: string }>();

    const chartConfig = {
        jigyohi: {
            label: '予算額',
            color: 'hsl(var(--chart-1))',
        },
    };

    const createdYear = lgconfig?.setting_m_lg_config[0].created_year;

    const initData = (data: FETCH_T_PDCA_ID | undefined) => {
        let jigyohiTransitions: {
            jigyoyear: number;
            jigyohi: number;
            wareki: string;
        }[] = [];

        if (!data) return;

        const tJigyo = data.pdca_t_pdca[0].t_jigyo;
        const lastYear = Number(createdYear) - 2;

        // 1. 前年度の事業費（yosan_gengaku、gen_kuri 0/1 を合算）
        const lastYearYosan = tJigyo.m_jisshi_keikaku.t_kessans
            ?.filter((yg: any) => yg.jigyo_year === lastYear)
            ?.reduce(
                (acc, cur) => ({
                    yosan_gengaku: (acc.yosan_gengaku || 0) + (cur.yosan_gengaku || 0),
                }),
                {} as {
                    yosan_gengaku: number;
                }
            );

        if (lastYearYosan && lastYearYosan.yosan_gengaku) {
            jigyohiTransitions.push({
                jigyoyear: lastYear,
                jigyohi: lastYearYosan.yosan_gengaku,
                wareki: ToWarekiFormatter(lastYear),
            });
        }

        // 2. 今年度の事業費（t_jigyohis から取得）
        tJigyo.m_jisshi_keikaku.t_jigyohis?.forEach((t_jigyohi: any) => {
            if (t_jigyohi.jigyo_year === Number(createdYear) && t_jigyohi.process_cd === 30) {
                jigyohiTransitions.push({
                    jigyoyear: t_jigyohi.jigyo_year,
                    jigyohi: t_jigyohi.jigyohi,
                    wareki: ToWarekiFormatter(t_jigyohi.jigyo_year),
                });
            }
        });

        tJigyo.m_jisshi_keikaku.t_jigyohis?.forEach((t_jigyohi: any) => {
            if (t_jigyohi.jigyo_year === Number(createdYear) - 1 && t_jigyohi.process_cd === 30) {
                jigyohiTransitions.push({
                    jigyoyear: t_jigyohi.jigyo_year,
                    jigyohi: t_jigyohi.jigyohi,
                    wareki: ToWarekiFormatter(t_jigyohi.jigyo_year),
                });
            }
        });

        // 3. 過去数年分の決算額（t_kessans）
        tJigyo.m_jisshi_keikaku.t_kessans?.forEach((item: any) => {
            // 過去4年前～前年より前の決算額
            if (item.jigyo_year < Number(createdYear) - 2 && Number(createdYear) - 4 < item.jigyo_year) {
                jigyohiTransitions.push({
                    jigyoyear: item.jigyo_year,
                    jigyohi: item.jigyohi,
                    wareki: ToWarekiFormatter(item.jigyo_year),
                });
            }
        });

        // 年度順にソートして state にセット
        const sortJigyohis = jigyohiTransitions
            .sort((a, b) => a.jigyoyear - b.jigyoyear)
            .map(({ jigyoyear, jigyohi, wareki }) => ({ jigyoyear, jigyohi, wareki }));

        setJigyohis(sortJigyohis);
    };

    // 初回データレンダリング時の処理
    useEffect(() => {
        if (data) {
            initData(data);
        }
    }, [data]);

    return (
        <Card>
            <CardHeader>
                <CardTitle>最終予算額推移(千円)</CardTitle>
                <CardDescription>{ToWarekiFormatter(Number(createdYear)) + 'は当初予算額を表示'}</CardDescription>
            </CardHeader>
            <CardContent>
                <ChartContainer config={chartConfig}>
                    <BarChart
                        accessibilityLayer
                        data={jigyohis}
                        margin={{
                            top: 20,
                        }}
                    >
                        <CartesianGrid vertical={false} />
                        <XAxis
                            dataKey="wareki"
                            tickLine={false}
                            tickMargin={10}
                            axisLine={false}
                            // tickFormatter={(value) => value.slice(0, 3)}
                        />
                        <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
                        <Bar
                            dataKey="jigyohi"
                            fill="hsl(var(--chart-1))"
                            radius={3}
                            label={{
                                position: 'top',
                                formatter: (value: number) => (value !== 0 ? value.toLocaleString() + '千円' : ''),
                            }}
                        >
                            {/* <LabelList position="top" offset={12} className="fill-foreground" fontSize={12} /> */}
                        </Bar>
                    </BarChart>
                </ChartContainer>
            </CardContent>
        </Card>
    );
}
